import { Gender, InsuredSubtype, PossibleFipsCode } from '@zorro/clients';
import {
  VALIDATION_MESSAGES,
  insuredMaxDateOfBirth,
  insuredMinDateOfBirth,
} from '@zorro/common-utils';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Divider } from '@zorro/design-system/v2/Divider';
import { RadioGroup } from '@zorro/design-system/v2/Radio';
import { Select } from '@zorro/design-system/v2/Select';
import { SsnPasswordInput } from '@zorro/design-system/v2/SsnPasswordInput';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { DateUtilInstance, getNow } from '@zorro/shared/formatters';
import { insuredSubtypeLabelConfig } from '@zorro/types';
import {
  AddressInput,
  DateInput,
  FormErrorMessage,
  Grid,
  Group,
  Text,
} from '@zorro/web/design-system';
import { getAdultDefaultDate } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

import { CitizenshipStatusInput } from '../CitizenshipStatusInput';
import { AnticipatedCareLevelInput } from './AnticipatedCareLevelInput';
import {
  InsuredFormGenderRadios,
  InsuredFormYesNoRadios,
} from './InsuredFormRadios';
import {
  INSURED_FORM_ADDRESS_TOOLTIP_LABEL,
  getInsuredBaseSchema,
} from './InsuredFormUtils';

const spouseRelationshipValues = Object.values([
  InsuredSubtype.SPOUSE,
  InsuredSubtype.DOMESTIC_PARTNER,
]);

const insuredTypeOptions = spouseRelationshipValues.map((status) => ({
  label: insuredSubtypeLabelConfig[status],
  value: status,
}));

export const getSpouseInsuredSchema = (
  isFinalizationMode: boolean,
  withAnticipatedCareLevel: boolean
) =>
  getInsuredBaseSchema(
    isFinalizationMode,
    false,
    withAnticipatedCareLevel
  ).concat(
    yup.object({
      fipsCode: yup.string().optional(),
      isDisabled: yup.boolean().optional().default(false),
      ...(isFinalizationMode
        ? {
            subtype: yup
              .mixed<InsuredSubtype>()
              .required(VALIDATION_MESSAGES.relationshipRequired)
              .oneOf(
                spouseRelationshipValues,
                VALIDATION_MESSAGES.relationshipRequired
              ),
          }
        : {
            subtype: yup
              .mixed<InsuredSubtype>()
              .oneOf(
                spouseRelationshipValues,
                VALIDATION_MESSAGES.relationshipRequired
              )
              .nullable(),
          }),
    })
  );

export type InsuredSpouseFormFields = yup.InferType<
  ReturnType<typeof getSpouseInsuredSchema>
>;

type Props = {
  isSpouseActive: boolean;
  spouseForm: UseFormReturn<InsuredSpouseFormFields>;
  targetEnrollmentDate?: DateUtilInstance;
  isFinalizationMode: boolean;
  isEmployeeMode: boolean;
  isDomesticPartnerAllowed: boolean;
  possibleFipsCodes: PossibleFipsCode[];
  setPossibleFipsCodes: (codes: PossibleFipsCode[]) => void;
  withAnticipatedCareLevel?: boolean;
  withResidentialAddress?: boolean;
};

export const SpouseFormInputs = ({
  isEmployeeMode,
  isFinalizationMode,
  isSpouseActive,
  spouseForm,
  targetEnrollmentDate,
  possibleFipsCodes,
  setPossibleFipsCodes,
  withAnticipatedCareLevel,
  isDomesticPartnerAllowed,
  withResidentialAddress = true,
}: Props) => {
  'use no memo';
  const { formState, control, watch, setValue } = spouseForm;
  const { errors } = formState;
  const isFemale = watch('gender') === Gender.FEMALE;
  const targetDate = targetEnrollmentDate || getNow().add(1, 'year');

  return (
    <Group>
      {isSpouseActive ? (
        <Grid>
          {!isDomesticPartnerAllowed && (
            <Grid.Col mb="var(--space-8)">
              <Alert variant="general-note">
                {isEmployeeMode
                  ? "You can add a legal spouse. Domestic partners aren't covered under your employer's plan this year"
                  : "This plan covers spouses only; domestic partners aren't eligible"}
              </Alert>
            </Grid.Col>
          )}
          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="firstName"
              render={({ field: { ...rest } }) => (
                <TextInput
                  {...rest}
                  label="First name"
                  placeholder="First name"
                  required
                  isMasked
                  size="lg"
                />
              )}
            />

            <FormErrorMessage errors={errors} fieldName="firstName" />
          </Grid.Col>

          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="lastName"
              render={({ field: { ...rest } }) => (
                <TextInput
                  {...rest}
                  label="Last name"
                  placeholder="Last name"
                  required
                  isMasked
                  size="lg"
                />
              )}
            />

            <FormErrorMessage errors={errors} fieldName="lastName" />
          </Grid.Col>

          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="subtype"
              render={({ field: { ...rest } }) => (
                <Select
                  {...rest}
                  label="Relationship"
                  placeholder="Select relationship"
                  disabled={!isDomesticPartnerAllowed}
                  data={insuredTypeOptions}
                  required={isFinalizationMode}
                  size="lg"
                />
              )}
            />

            <FormErrorMessage errors={errors} fieldName="subtype" />
          </Grid.Col>

          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="dateOfBirth"
              render={({ field: { ...rest } }) => (
                <DateInput
                  {...rest}
                  label="Date of birth"
                  defaultLevel="decade"
                  defaultDate={getAdultDefaultDate()}
                  popoverProps={{ withinPortal: false }}
                  minDate={insuredMinDateOfBirth(false, targetDate)}
                  maxDate={insuredMaxDateOfBirth(false, targetDate)}
                  withCalendarIcon={false}
                  styles={{
                    input: { height: '3rem', minHeight: '3rem' },
                    label: { marginBottom: '0.5rem' },
                  }}
                  size="lg"
                  required
                />
              )}
            />

            <FormErrorMessage errors={errors} fieldName="dateOfBirth" />
          </Grid.Col>

          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  label="Gender"
                  required={isFinalizationMode}
                >
                  <InsuredFormGenderRadios />
                </RadioGroup>
              )}
            />

            <FormErrorMessage errors={errors} fieldName="gender" />
          </Grid.Col>

          <Grid.Col span={{ sm: 6 }}>
            <Divider hiddenFrom="sm" mt="var(--space-8)" mb="var(--space-16)" />

            <Controller
              control={control}
              name="isSmoker"
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  label="Tobacco user"
                  required={isFinalizationMode}
                >
                  <InsuredFormYesNoRadios />
                </RadioGroup>
              )}
            />

            <FormErrorMessage errors={errors} fieldName="isSmoker" />
          </Grid.Col>

          {isFemale && (
            <>
              <Grid.Col span={{ sm: 6 }}>
                <Divider
                  hiddenFrom="sm"
                  mt="var(--space-8)"
                  mb="var(--space-16)"
                />

                <Controller
                  control={control}
                  name="isPregnant"
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      label="Pregnant"
                      required={isFinalizationMode}
                    >
                      <InsuredFormYesNoRadios />
                    </RadioGroup>
                  )}
                />

                <FormErrorMessage errors={errors} fieldName="isPregnant" />
              </Grid.Col>
              <Grid.Col span={{ sm: 6 }} />
            </>
          )}

          <Grid.Col span={{ sm: 6 }}>
            <Controller
              control={control}
              name="ssn"
              render={({ field: { value, ...rest } }) => (
                <SsnPasswordInput
                  {...rest}
                  label="SSN"
                  value={value || ''}
                  required={isFinalizationMode}
                  size="lg"
                />
              )}
            />

            <FormErrorMessage errors={errors} fieldName="ssn" />
          </Grid.Col>

          <Grid.Col span={{ sm: 6 }}>
            <Controller
              name="citizenshipStatus"
              control={control}
              render={({ field }) => (
                <CitizenshipStatusInput
                  {...field}
                  selectProps={{
                    required: isFinalizationMode,
                    clearable: !isFinalizationMode,
                  }}
                />
              )}
            />

            <FormErrorMessage errors={errors} fieldName="citizenshipStatus" />
          </Grid.Col>

          {withResidentialAddress && (
            <>
              <Grid.Col>
                <Controller
                  control={control}
                  name="residentialAddress"
                  render={({ field: { value, onBlur, onChange, ...rest } }) => (
                    <AddressInput
                      {...rest}
                      onBlur={() => {
                        onChange(value);
                        onBlur();
                        setValue('fipsCode', undefined);
                        setPossibleFipsCodes([]);
                      }}
                      notifySubscribers={(newAddress) => onChange(newAddress)}
                      onChange={onChange}
                      label={
                        <Tooltip
                          label={INSURED_FORM_ADDRESS_TOOLTIP_LABEL}
                          disabled={!isEmployeeMode}
                          position="top"
                          maw={400}
                        >
                          <span>Residential address</span>
                        </Tooltip>
                      }
                      value={value || ''}
                      placeholder="Type residential address"
                      required={isFinalizationMode}
                      disabled={isEmployeeMode}
                      isMasked
                    />
                  )}
                />

                <FormErrorMessage
                  errors={errors}
                  fieldName="residentialAddress"
                />
              </Grid.Col>
              {possibleFipsCodes.length > 1 && (
                <Grid.Col>
                  <Controller
                    control={control}
                    name="fipsCode"
                    render={({ field: { ...rest } }) => (
                      <Select
                        {...rest}
                        label="County of residence"
                        placeholder="Select county of residence"
                        data={possibleFipsCodes.map(({ fipsCode, county }) => ({
                          label: county,
                          value: fipsCode,
                        }))}
                      />
                    )}
                  />
                  <FormErrorMessage errors={errors} fieldName="fipsCode" />
                </Grid.Col>
              )}
            </>
          )}
          {withAnticipatedCareLevel && (
            <Grid.Col>
              <Divider mt="var(--space-12)" mb="var(--space-20)" />
              <Controller
                control={control}
                name="anticipatedCareLevel"
                render={({ field }) => (
                  <AnticipatedCareLevelInput
                    {...field}
                    isRequired={isFinalizationMode}
                  />
                )}
              />
              <FormErrorMessage
                errors={errors}
                fieldName="anticipatedCareLevel"
              />
            </Grid.Col>
          )}
        </Grid>
      ) : (
        <Text ta="center" c="zorroIris.9">
          + Add spouse / domestic partner
        </Text>
      )}
    </Group>
  );
};
